import { create } from "zustand";
import { ApiService } from "../api";

/**
 * How much is waiting on this user — the number beside "My Actions" in the
 * sidebar, and the count of *overdue* work beside it.
 *
 * **It counts tasks, and it counts them the way the page does.** It used to
 * count requests ("one permit counts once, however many things it needs from
 * you"), which was the only number available while the unit of work was derived
 * at read time. Now that a task is a row, the badge and the inbox read the same
 * table — a badge saying 3 over a list of 5 rows is a badge nobody trusts.
 *
 * `overdue` is carried separately because it is the number that changes what
 * somebody does next, and it is the one thing the old badge could never say.
 *
 * It lives in a store rather than in the layout because the sidebar sits
 * *outside* the tabbed workspace. It never remounts and no page can re-render
 * it, so approving something in a request tab would otherwise leave the number
 * stale until a hard reload. Anything that can change what is on the desk calls
 * `refreshMyActionsCount()`.
 */
type MyActionsState = {
  /** Open tasks owed by this user. 0 until the first answer arrives. */
  count: number;
  /** How many of them are past their deadline. */
  overdue: number;
  refresh: () => Promise<void>;
  reset: () => void;
};

// Focus, interval and a page load can land together; one answer is enough.
let inFlight: Promise<void> | null = null;

export const useMyActionsStore = create<MyActionsState>((set) => ({
  count: 0,
  overdue: 0,

  reset: () => set({ count: 0, overdue: 0 }),

  refresh: async () => {
    if (inFlight) return inFlight;

    inFlight = (async () => {
      try {
        // One endpoint returning the counts already folded, rather than a page
        // of rows to be counted client-side: the badge needs four integers and
        // no row at all.
        const summary = await ApiService.apiMainTasksSummary();
        set({ count: summary.open ?? 0, overdue: summary.overdue ?? 0 });
      } catch {
        // A badge is not worth an error toast, and a failed count must never
        // take the sidebar down with it. Keep the last number we knew.
      } finally {
        inFlight = null;
      }
    })();

    return inFlight;
  },
}));

/** Re-read the count. Safe to call from anywhere, including outside React. */
export const refreshMyActionsCount = () => useMyActionsStore.getState().refresh();

/** Clear it on logout, so the next person to sign in never sees it. */
export const resetMyActionsCount = () => useMyActionsStore.getState().reset();
